import { GetServerSideProps, NextPage } from 'next';

import getMetadata, {
  MetadataResponse,
} from '@wetransfer/transfer/api/metadata';
import ClientSideApp from '@wetransfer/transfer/ClientSideApplication';
import { getRedirectToChannelAttributes } from '@wetransfer/transfer/helper/channel';
import fetchInitialData, {
  InitialData,
} from '@wetransfer/transfer/helper/fetchInitialData';
import setLocaleFromRequest from '@wetransfer/transfer/helper/setLocaleFromRequest';
import useTransferOpenGraph from '@wetransfer/transfer/hooks/useTransferOpenGraph';
import Head from '@wetransfer/transfer-new/components/Head';

interface PageProps {
  metadata: MetadataResponse | null;
  securityHash?: string;
  data: InitialData;
}

export const getServerSideProps: GetServerSideProps<
  PageProps,
  {
    transferId: string;
    /* Because of how next.js dynamic params work. We can only have one name for the second parameter of the page.
     Be aware the **recipientId** in this case would the **securityHash** */
    recipientId: string;
  }
> = async ({ params, req }) => {
  setLocaleFromRequest(req);

  const data = await fetchInitialData(req);

  if (data?.redirect) {
    return {
      redirect: {
        ...data.redirect,
      },
    };
  }

  if (params === undefined) {
    return {
      props: {
        metadata: null,
        data,
      },
    };
  }

  const metadata = await getMetadata({
    transferId: params.transferId,

    // Sending the "recipientId" as a securityHash is correct here
    securityHash: params.recipientId,
  });

  // Redirect users to download link with a subdomain
  const redirectToChannelAttributes = getRedirectToChannelAttributes(
    metadata?.transfer_channel_name,
    req.url,
    req.headers.host
  );

  if (redirectToChannelAttributes !== null) {
    return redirectToChannelAttributes;
  }

  return {
    props: {
      metadata,
      securityHash: params.recipientId,
      data,
    },
  };
};

const Page: NextPage<PageProps> = ({ data, metadata, securityHash }) => {
  const openGraph = useTransferOpenGraph({ metadata, securityHash });

  return (
    <>
      <Head openGraph={openGraph} />
      <ClientSideApp data={data} />
    </>
  );
};

export default Page;
